import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CalendarDays, Home, LogOut, Settings, ShieldCheck, Users } from "lucide-react";
import { Link, NavLink, Outlet, useNavigate } from "react-router";
import { Brand } from "@/components/brand";
import { signOut as signOutEverywhere } from "@/lib/auth";
import { meQuery, supportSummaryQuery } from "@/lib/queries";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/games", label: "Games", icon: CalendarDays, end: false },
  { to: "/app/groups", label: "Groups", icon: Users, end: false },
  { to: "/app/settings", label: "Settings", icon: Settings, end: false },
];

export function AppShell() {
  const { data: me } = useQuery(meQuery);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const signOut = async () => {
    await signOutEverywhere();
    queryClient.clear();
    navigate("/", { replace: true });
  };

  const unread = me?.unreadNotifications ?? 0;
  const overdue =
    useQuery({ ...supportSummaryQuery, enabled: Boolean(me?.siteAdmin) }).data?.overdue ?? 0;

  return (
    <div className="min-h-dvh md:grid md:grid-cols-[15rem_1fr]">
      <aside className="sticky top-0 hidden h-dvh flex-col border-r border-border bg-surface px-3 py-5 md:flex">
        <Link to="/app" className="px-2">
          <Brand />
        </Link>
        <nav className="mt-8 flex flex-col gap-1" aria-label="Main">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted hover:bg-surface-2 hover:text-foreground",
                  isActive && "bg-accent text-accent-foreground hover:bg-accent",
                )
              }
            >
              <Icon className="size-5" aria-hidden />
              {label}
            </NavLink>
          ))}
          {me?.siteAdmin ? (
            <NavLink
              to="/app/admin"
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted hover:bg-surface-2 hover:text-foreground",
                  isActive && "bg-accent text-accent-foreground hover:bg-accent",
                )
              }
            >
              <ShieldCheck className="size-5" aria-hidden />
              Admin
              {overdue > 0 ? (
                <span className="ml-auto grid min-w-5 place-items-center rounded-full bg-danger-bg px-1.5 text-xs font-bold text-danger">
                  {overdue}
                  <span className="sr-only"> overdue support tickets</span>
                </span>
              ) : null}
            </NavLink>
          ) : null}
          <NavLink
            to="/app/notifications"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted hover:bg-surface-2 hover:text-foreground",
                isActive && "bg-accent text-accent-foreground hover:bg-accent",
              )
            }
          >
            <Bell className="size-5" aria-hidden />
            Notifications
            {unread > 0 ? (
              <span className="ml-auto rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground">
                {unread}
              </span>
            ) : null}
          </NavLink>
        </nav>
        <div className="mt-auto rounded-xl border border-border p-3 text-sm">
          <p className="truncate font-semibold">{me?.user.name}</p>
          <p className="truncate text-muted">{me?.plan === "premium" ? "Premium" : "Free plan"}</p>
          <button
            type="button"
            onClick={signOut}
            className="mt-2 flex items-center gap-2 text-muted hover:text-foreground"
          >
            <LogOut className="size-4" aria-hidden /> Sign out
          </button>
        </div>
      </aside>

      <div className="min-w-0">
        <header className="sticky top-0 z-30 flex h-[calc(3.5rem+env(safe-area-inset-top))] items-center justify-between border-b border-border bg-background/90 pr-16 pl-4 pt-[env(safe-area-inset-top)] backdrop-blur md:hidden">
          <Link to="/app" aria-label="GridAction home">
            <Brand />
          </Link>
          <div className="flex items-center gap-1">
            <Link
              to="/app/notifications"
              className="relative rounded-full p-2.5 text-foreground hover:bg-surface-2"
              aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
            >
              <Bell className="size-5" />
              {unread > 0 ? (
                <span className="absolute top-1.5 right-1.5 min-w-4 rounded-full bg-primary px-1 text-center text-[10px] leading-4 font-bold text-primary-foreground">
                  {unread > 9 ? "9+" : unread}
                </span>
              ) : null}
            </Link>
          </div>
        </header>

        <main className="mx-auto w-full max-w-5xl px-4 pt-5 pb-28 md:px-8 md:pt-16 md:pb-12">
          <Outlet />
        </main>

        <nav
          className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-border bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
          aria-label="Main"
        >
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium text-muted",
                  isActive && "text-primary",
                )
              }
            >
              <Icon className="size-6" aria-hidden />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
